import React from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

// Builds a rich, revision-aware timeline from the case status + submission/change counts.
export const CaseStatusTimeline = ({ status, versionCount = 0, changesRequestedCount = 0 }) => {
  const { t } = useTranslation();
  const hasVersions = versionCount > 0 || status === "Submitted" || status === "Completed";

  const steps = [
    { key: "draft", label: t("timeline.draft") },
    { key: "published", label: t("timeline.published") },
    { key: "selected", label: t("timeline.selected") },
    { key: "inProgress", label: t("timeline.inProgress") },
  ];
  if (hasVersions) {
    steps.push({ key: "firstVersion", label: t("timeline.firstVersion") });
    steps.push({ key: "review-0", label: t("timeline.inReview") });
  }
  for (let i = 0; i < changesRequestedCount; i++) {
    steps.push({ key: `changes-${i}`, label: t("timeline.changesRequested") });
    steps.push({ key: `newv-${i}`, label: t("timeline.newVersion") });
    steps.push({ key: `review-${i + 1}`, label: t("timeline.inReview") });
  }
  steps.push({ key: "completed", label: t("timeline.completed") });

  const keyIndex = (k) => steps.findIndex((s) => s.key === k);
  const lastReviewIndex = () => {
    for (let i = steps.length - 1; i >= 0; i--) if (steps[i].key.startsWith("review-")) return i;
    return 3;
  };

  let currentIndex = 3;
  if (status === "Draft") currentIndex = 0;
  else if (status === "Published") currentIndex = 1;
  else if (status === "Designer Selected") currentIndex = 2;
  else if (status === "Completed") currentIndex = steps.length - 1;
  else if (status === "Submitted") currentIndex = lastReviewIndex();
  else if (status === "In Progress") {
    currentIndex = changesRequestedCount > 0 ? keyIndex(`newv-${changesRequestedCount - 1}`) : 3;
  }

  return (
    <div className="flex flex-col gap-0" data-testid="case-status-timeline">
      {steps.map((s, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        const isChange = s.key.startsWith("changes-");
        return (
          <div key={s.key} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors ${
                  done
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : active
                    ? isChange
                      ? "border-amber-500 bg-amber-500 text-white"
                      : "border-brand bg-brand text-white"
                    : "border-border bg-background text-muted-foreground"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`h-6 w-0.5 ${i < currentIndex ? "bg-emerald-500" : "bg-border"}`} />
              )}
            </div>
            <span
              className={`pt-0.5 text-sm ${
                active ? "font-semibold text-foreground" : done ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};
